// PregnancyCalculator.js
import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import PregnancyEducationalContent from "./PregnancyEducationalContent";
const TITLE = `Kalkulator Kehamilan - Hitung Tanggal Lahir dengan Akurat`;
const DESCRIPTION = `Kalkulator kehamilan akurat untuk menghitung tanggal perkiraan lahir dan usia kehamilan dalam minggu dan bulan. Masukkan tanggal haid terakhir untuk mengetahui tanggal lahir, tahap pertumbuhan janin, dan hari ovulasi. Kalkulator kehamilan akurat dengan kalender Masehi dan Hijriah serta penjelasan detail setiap tahap kehamilan.`;
const KEYWORDS = `kalkulator kehamilan, hitung tanggal lahir, kalkulator kehamilan akurat, hitung kehamilan dalam minggu, hitung kehamilan dalam bulan, kalkulator kehamilan hijriah, tanggal perkiraan lahir, usia kehamilan, tahap kehamilan, hari ovulasi, hitung masa kehamilan, pregnancy calculator, kapan saya melahirkan, kalkulator kelahiran, hitung minggu kehamilan`;
const OG_URL = "https://do-calculate.com/calculator/id/pregnancy/";

const GREGORIAN_MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];
function PregnancyCalculator() {
  const [dateInput, setDateInput] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [cycleLength, setCycleLength] = useState(28);
  const [lastPeriodError, setLastPeriodError] = useState("");
  const [cycleLengthError, setCycleLengthError] = useState("");
  const [result, setResult] = useState(null);

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const getDayOfWeek = (date) => {
    const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
    return days[date.getDay()];
  };

  const getSeason = (month) => {
    if (month >= 3 && month <= 5) return "Musim Semi";
    if (month >= 6 && month <= 8) return "Musim Panas";
    if (month >= 9 && month <= 11) return "Musim Gugur";
    return "Musim Dingin";
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateDaysBetween = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const newDateInput = { ...dateInput, [name]: value };
    setDateInput(newDateInput);

    const selectedDate = new Date(newDateInput.year, newDateInput.month - 1, newDateInput.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setLastPeriodError("Tanggal harus di masa lalu");
    } else {
      const diffDays = calculateDaysBetween(selectedDate, today);
      if (diffDays > 365) {
        setLastPeriodError("Tanggal tampak terlalu jauh di masa lalu");
      } else {
        setLastPeriodError("");
      }
    }
  };

  const handleCycleLengthChange = (event) => {
    const value = event.target.value;
    if (value !== "") {
      const parsedValue = parseInt(value);
      if (!isNaN(parsedValue) && parsedValue >= 20 && parsedValue <= 45) {
        setCycleLength(parsedValue);
        setCycleLengthError("");
      } else {
        setCycleLength(value);
        if (isNaN(parsedValue)) {
          setCycleLengthError("Pastikan memasukkan angka yang valid");
        } else if (parsedValue < 20) {
          setCycleLengthError("Panjang siklus harus minimal 20 hari");
        } else if (parsedValue > 45) {
          setCycleLengthError("Panjang siklus tidak boleh lebih dari 45 hari");
        }
      }
    } else {
      setCycleLength("");
      setCycleLengthError("");
    }
  };

  const handleReset = () => {
    setDateInput({
      day: new Date().getDate(),
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setCycleLength(28);
    setLastPeriodError("");
    setCycleLengthError("");
    setResult(null);
  };

  const calculatePregnancy = (e) => {
    e.preventDefault();
    const lmpDate = new Date(dateInput.year, dateInput.month - 1, dateInput.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(lmpDate.getTime()) || lastPeriodError) return;

    const conceptionDate = addDays(lmpDate, cycleLength - 14);
    const dueDate = addDays(lmpDate, 280);
    const daysSinceLMP = calculateDaysBetween(lmpDate, today);
    const weeksSinceLMP = Math.floor(daysSinceLMP / 7);
    const remainingDays = daysSinceLMP % 7;
    const daysUntilDue = calculateDaysBetween(today, dueDate);
    const weeksUntilDue = Math.floor(daysUntilDue / 7);
    const remainingDaysUntilDue = daysUntilDue % 7;

    let trimester = weeksSinceLMP < 13 ? "Trimester Pertama" : weeksSinceLMP < 27 ? "Trimester Kedua" : "Trimester Ketiga";

    setResult({
      lmpDate: formatDate(lmpDate),
      conceptionDate: formatDate(conceptionDate),
      dueDate: formatDate(dueDate),
      dueDateDay: getDayOfWeek(dueDate),
      dueDateSeason: getSeason(dueDate.getMonth() + 1),
      currentWeek: weeksSinceLMP,
      currentDay: remainingDays,
      currentMonth: Math.floor(weeksSinceLMP / 4) + 1,
      trimester: trimester,
      daysUntilDue: daysUntilDue,
      weeksUntilDue: weeksUntilDue,
      remainingDaysUntilDue: remainingDaysUntilDue,
      totalDays: daysSinceLMP
    });
  };

  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Halaman Utama",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Kalkulator",
          item: "https://do-calculate.com/id/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Kalkulator Kehamilan",
          item: OG_URL,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Bagaimana cara menghitung tanggal lahir?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Untuk menghitung tanggal lahir, masukkan tanggal hari pertama haid terakhir ke dalam kalkulator kehamilan. Tambahkan 280 hari (40 minggu) untuk menentukan tanggal perkiraan lahir menggunakan aturan medis Naegele.",
          },
        },
        {
          "@type": "Question",
          name: "Berapa durasi kehamilan normal?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Durasi kehamilan normal berkisar antara 37 hingga 42 minggu, dengan rata-rata 40 minggu (280 hari) dari hari pertama haid terakhir. Sebagian besar kelahiran terjadi antara minggu ke-38 dan minggu ke-42.",
          },
        },
        {
          "@type": "Question",
          name: "Bagaimana saya tahu minggu kehamilan saya saat ini?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Untuk mengetahui minggu kehamilan saat ini, hitung jumlah minggu dari tanggal hari pertama haid terakhir hingga hari ini. Gunakan kalkulator kehamilan untuk mendapatkan hasil yang akurat dengan informasi tambahan tentang tahap pertumbuhan janin.",
          },
        },
        {
          "@type": "Question",
          name: "Apa perbedaan antara menghitung kehamilan dalam minggu dan bulan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Perhitungan kehamilan dalam minggu lebih akurat secara medis dan digunakan dokter untuk memantau pertumbuhan janin. Perhitungan dalam bulan lebih mudah dipahami di mana setiap 4 minggu kira-kira sama dengan satu bulan, tetapi dokter lebih memilih minggu untuk akurasi.",
          },
        },
        {
          "@type": "Question",
          name: "Apakah kalkulator kehamilan akurat?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Kalkulator kehamilan akurat jika siklus menstruasi Anda teratur dan Anda mengingat tanggal haid terakhir dengan tepat. Untuk kepastian penuh, Anda harus mengunjungi dokter untuk melakukan USG yang menentukan usia kehamilan dengan lebih akurat.",
          },
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Kalkulator Kehamilan",
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, faqSchema, calculatorSchema];
  };
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Kehamilan</strong> adalah alat medis gratis dan akurat untuk menghitung{" "}
                <strong>tanggal perkiraan lahir</strong> dan menentukan{" "}
                <strong>usia kehamilan</strong> dalam minggu dan bulan. Yang Anda perlukan adalah
                memasukkan tanggal hari pertama haid terakhir untuk mendapatkan informasi lengkap
                tentang kehamilan Anda, termasuk <strong>tahap pertumbuhan janin</strong> dan pemeriksaan yang diperlukan
                pada setiap tahap.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    Hitung Tanggal Lahir
                  </h2>
                </div>
                <form onSubmit={calculatePregnancy}>
                  <div className="form-group mb-3">
                    <label ><strong>Hari pertama haid terakhir:</strong></label>
                    <div className="d-flex gap-2">
                      <select style={{width:'100px'}} className="form-control" name="day" value={dateInput.day} onChange={handleDropdownChange}>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select style={{width:'100%'}} className="form-control" name="month" value={dateInput.month} onChange={handleDropdownChange}>
                        {GREGORIAN_MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>)}
                      </select>
                      <select style={{width:'100px'}} className="form-control" name="year" value={dateInput.year} onChange={handleDropdownChange}>
                        {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    {lastPeriodError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{lastPeriodError}</div>}
                  </div>

                  <div className="form-group mb-4">
                    <label ><strong>Rata-rata panjang siklus menstruasi (hari):</strong></label>
                    <div className="d-flex align-items-center">
                      <input type="number" className="form-control" value={cycleLength} onChange={handleCycleLengthChange} min="20" max="45" required style={{ maxWidth: "150px" }} />
                      <span style={{ marginRight: "10px", padding: "8px 15px", border: "1px solid #ced4da", borderRadius: "4px", backgroundColor: "#f9f9f9" }}>Hari</span>
                    </div>
                    {cycleLengthError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{cycleLengthError}</div>}
                  </div>

                  <hr />
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn bg-success text-white" style={{ padding: "10px 30px" }}>Hitung</button>
                    <button type="button" className="btn btn-secondary" onClick={handleReset} style={{ padding: "10px 30px", marginRight: "10px" }}>Hapus</button>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-6">
                {result && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>Hasil</h3>
                    </div>
                    <div className="alert alert-success" style={{ fontSize: "16px" }}>
                      <table className="table table-bordered" style={{ width: "100%", marginBottom: 0 }}>
                        <tbody>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#e7f3fe", padding: "12px" }}>
                              <strong>Tanggal Perkiraan Lahir:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#e7f3fe", padding: "12px" }}>
                              <strong style={{ fontSize: "18px", color: "#1a5f3f" }}>
                                {result.dueDate}
                              </strong>
                              <br />
                              <small>Hari {result.dueDateDay} di {result.dueDateSeason}</small>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#fff3cd", padding: "12px" }}>
                              <strong>Usia Kehamilan Saat Ini:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#fff3cd", padding: "12px" }}>
                              <strong style={{ fontSize: "18px", color: "#856404" }}>
                                {result.currentWeek} minggu {result.currentDay} hari
                              </strong>
                              <br />
                              <small>({result.currentMonth} bulan kira-kira)</small>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#d1ecf1", padding: "12px" }}>
                              <strong>Tahap Saat Ini:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#d1ecf1", padding: "12px" }}>
                              <strong style={{ fontSize: "18px", color: "#0c5460" }}>
                                {result.trimester}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#d4edda", padding: "12px" }}>
                              <strong>Sisa Waktu:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#d4edda", padding: "12px" }}>
                              <strong style={{ fontSize: "18px", color: "#155724" }}>
                                {result.weeksUntilDue} minggu {result.remainingDaysUntilDue} hari
                              </strong>
                              <br />
                              <small>({result.daysUntilDue} hari)</small>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#f8d7da", padding: "12px" }}>
                              <strong>Tanggal Pembuahan yang Diperkirakan:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#f8d7da", padding: "12px" }}>
                              <strong style={{ fontSize: "16px", color: "#721c24" }}>
                                {result.conceptionDate}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: "center", backgroundColor: "#d4edda", padding: "12px" }}>
                              <strong>Anda Telah Menyelesaikan:</strong>
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: "#d4edda", padding: "12px" }}>
                              <strong style={{ fontSize: "16px", color: "#0c5460" }}>
                                {((result.totalDays / 280) * 100).toFixed(2)}%
                                dari masa kehamilan
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              <strong>💡 Catatan Penting:</strong> Hasil ini bersifat perkiraan berdasarkan aturan medis Naegele.
              Untuk menentukan tanggal lahir dengan lebih akurat, disarankan mengunjungi dokter dan melakukan USG pada minggu-minggu pertama kehamilan.
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      <PregnancyEducationalContent />
      <br />
    </div>
  );
}
export default PregnancyCalculator;